import { useHttpQuery } from '@/resources/http/useHttpQuery';
import { useHttpMutation } from '@/resources/mutation/useHttpMutation';
import { t, Trans } from '@lingui/macro';
import { FieldSelectControlled, Flexbox, FormItem, SecondaryButton, TertiaryButton } from '@luminovo/design-system';
import { UserRoleMembershipInsert } from '@luminovo/http-client';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormContainer } from '../../../components/formLayouts/FormContainer';
import { SubmitButton } from '../../../components/formLayouts/SubmitButton';

export interface UpdateUserRoleFormState {
    userId: string;
    oldRole: UpdateUserRoleOption | null;
    role: UpdateUserRoleOption;
    removeAllRoles?: boolean;
}

export interface UpdateUserRoleOption {
    id: string;
    name: string;
}

export function UpdateUserRoleForm({ userId, closeDialog }: { userId: string; closeDialog: () => void }) {
    // Fetch available roles
    const { data: roles = [] } = useHttpQuery(
        'GET /roles',
        {},
        {
            select: (data) =>
                data.filter((role) => !role.is_system_role).sort((a, b) => a.name.localeCompare(b.name)) || [],
        },
    );

    // Fetch the user's current role
    const { data: userRoleMemberships = {} } = useHttpQuery(
        'GET /user_role_memberships',
        {
            queryParams: {
                user_ids: [userId],
            },
        },
        {
            select: (data) => data || {},
            enabled: !!userId,
        },
    );

    // Set up mutation for updating user role
    const updateUserRoleMutation = useHttpMutation('PATCH /user_role_memberships', {
        snackbarMessage: t`User role updated successfully`,
    });

    const handleSubmit = async (form: UpdateUserRoleFormState) => {
        // Handle the case when user wants to remove all roles
        if (form.removeAllRoles && form.oldRole) {
            await updateUserRoleMutation.mutateAsync({
                requestBody: {
                    inserts: [],
                    deletes: [
                        {
                            user_id: form.userId,
                            role_id: form.oldRole.id,
                        },
                    ],
                },
            });
            closeDialog();
            return;
        }

        // Only proceed if the role has changed
        if (form.role.id !== form.oldRole?.id) {
            const inserts: UserRoleMembershipInsert[] = [
                {
                    user_id: form.userId,
                    role_id: form.role.id,
                },
            ];

            // Always include the old role in deletes if it exists
            const deletes: UserRoleMembershipInsert[] = form.oldRole
                ? [
                      {
                          user_id: form.userId,
                          role_id: form.oldRole.id,
                      },
                  ]
                : [];

            await updateUserRoleMutation.mutateAsync({
                requestBody: {
                    inserts,
                    deletes,
                },
            });
        }

        closeDialog();
    };

    const options = roles.map((role) => ({ id: role.id, name: role.name }));

    // Find the user's current role
    const userRoleIds = userRoleMemberships[userId]?.map((membership) => membership.role_id) || [];
    const currentUserRole = userRoleIds.length > 0 ? roles.find((role) => role.id === userRoleIds[0]) : null;

    // Convert to the form option format
    const currentRoleOption = currentUserRole ? { id: currentUserRole.id, name: currentUserRole.name } : null;

    const initialValues: UpdateUserRoleFormState = {
        userId: userId,
        oldRole: currentRoleOption,
        role: currentRoleOption || options[0],
        removeAllRoles: false,
    };

    return (
        <FormContainer onSubmit={handleSubmit} defaultValues={initialValues} key={currentRoleOption?.id || 'no-role'}>
            <UpdateUserRoleFormInner
                options={options}
                onCancel={() => closeDialog()}
                currentRoleOption={currentRoleOption}
            />
        </FormContainer>
    );
}

const UpdateUserRoleFormInner = ({
    options,
    onCancel,
    currentRoleOption,
}: {
    options: UpdateUserRoleOption[];
    onCancel: () => void;
    currentRoleOption: UpdateUserRoleOption | null;
}) => {
    const { control, setValue } = useFormContext<UpdateUserRoleFormState>();
    const [removeAllRoles, setRemoveAllRoles] = useState(false);

    const handleRemoveAllRoles = () => {
        setRemoveAllRoles(true);
        setValue('removeAllRoles', true);
    };

    return (
        <Flexbox flexDirection="column" gap={24}>
            {!removeAllRoles && (
                <FormItem label={t`Role`} required>
                    <FieldSelectControlled
                        name="role"
                        required
                        control={control}
                        FieldProps={{
                            options: options,
                            getOptionLabel: (option) => option.name,
                            placeholder: t`Select a role`,
                        }}
                    />
                </FormItem>
            )}

            {removeAllRoles && currentRoleOption && (
                <Flexbox flexDirection="column" gap={8}>
                    <FormItem label={t`Current Role`}>
                        <div>{currentRoleOption.name}</div>
                    </FormItem>
                    <div>
                        <Trans>User will have no additional role assigned.</Trans>
                    </div>
                </Flexbox>
            )}

            {!currentRoleOption && !removeAllRoles && (
                <Flexbox flexDirection="column" gap={8}>
                    <div>
                        <Trans>This user currently has no role assigned.</Trans>
                    </div>
                </Flexbox>
            )}

            <Flexbox gap={8} justifyContent="space-between" flexWrap="wrap">
                <Flexbox gap={8}>
                    <SecondaryButton onClick={onCancel}>
                        <Trans>Cancel</Trans>
                    </SecondaryButton>
                    {!removeAllRoles && currentRoleOption && (
                        <TertiaryButton onClick={handleRemoveAllRoles} type="button">
                            <Trans>Set no additional role for this user</Trans>
                        </TertiaryButton>
                    )}
                </Flexbox>
                <SubmitButton />
            </Flexbox>
        </Flexbox>
    );
};
